/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { LookupService as CoreService } from 'app/core/services/lookup-service';
import { getReference } from 'app/shared/models/essette-types';
import { Toast, Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { localSort } from 'app/shared/lib/sorting';
import { allActionsSucceeded } from 'app/shared/lib/api/api-results';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

// TODO [Matt] Where does this belong?
function decimal(digits: number, precision: number) {
    return Math.min(
        Number(`${'9'.repeat(digits - precision)}.${'9'.repeat(Math.min(15 - digits + precision, precision))}`),
        Number.MAX_SAFE_INTEGER
    );
}

@autoinject()
export class SaveHouseholdCaseService extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    @bindable.boolean()
    protected readonly: boolean = false;

    protected caseService: CM.CaseService | null = null;
    protected maxNumber: number = Number.MAX_SAFE_INTEGER;
    protected modifiers: CD.AuthModifier[] = [];
    protected placesOfService: CD.PlaceOfService[] = [];
    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];
    protected localSorting = new Sorting();

    public submittedChanges: CM.CaseService | null = null;

    public serviceSearchQuery: string | null = null;          // the value of the autocomplete text box
    private serviceSearchResults: CD.Service[] = [];     // array of search results from API for autocomplete (not currently used)

    public unitSearchQuery: string | null = null;          // the value of the autocomplete text box
    private unitSearchResults: CD.Service[] = [];     // array of search results from API for autocomplete (not currently used)

    protected decimal = decimal;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly lookupService: LookupService,
        private readonly coreService: CoreService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.householdCaseId) throw new Error('Bindable property "householdCaseId" must be bound');
    }

    protected attached(): void {
        // load lookups, do not wait for results
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected async selectService(service: CD.Service): Promise<void> {
        if(!this.caseService) { this.caseService = {} }
        this.caseService.service = service;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected async selectUnit(unit: Core.UnitOfMeasure): Promise<void> {
        if(!this.caseService) { this.caseService = {} }
        if(!this.caseService.quantity) {
            this.caseService.quantity = {};
        }
        this.caseService.quantity.unit = unit;
        this.unitSearchQuery = null;
        this.unitSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected clearService(): void {
        this.serviceSearchResults.splice(0);
        this.caseService!.service = null;
        this.serviceSearchQuery = null;
    }

    ////****************** Functions ************************************************////
    protected serviceDisplay(service: CD.Service) {
        if(!service || !service.code || !service.description) return '';
        return `${service.code} - ${service.description}`;
    }

    private constructSaveObject(memberCase?: CM.Case): CM.CaseService {
        let saveService: CM.CaseService = {
            id: this.caseService?.id,
            case: memberCase ? getReference(memberCase) : { id: this.householdCaseId },
            service: getReference(this.caseService?.service),
            dateTimeRange: this.caseService?.dateTimeRange,
            placeOfService: getReference(this.caseService?.placeOfService),
            modifier: getReference(this.caseService?.modifier),
            charges: this.caseService?.charges,
            quantity: {
                value: this.caseService?.quantity?.value,
                unit: getReference(this.caseService?.quantity?.unit)
            },
            vendor: {
                name: { full: this.caseService?.vendor?.name?.full },
                identifier: { primary: this.caseService?.vendor?.identifier?.primary }
            }
        }

        return saveService;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const success = await this.insertCaseService()
        if(success) {
            this.submittedChanges = this.caseService;
        }
        return success;
    }

    public reset(): void {
        this.selectedCases.splice(0);
        this.serviceSearchResults.splice(0);
        this.unitSearchResults.splice(0);
        this.caseService = null;
        this.serviceSearchQuery = null;
        this.unitSearchQuery = null;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;
        try {
            this.state.beginLoad();
            const options: Partial<IQueryLoadOptions> = { all: true };
            const caseIncludes: string[] = ['member'];
            const placeOfServiceSorting = new Sorting();
            placeOfServiceSorting.add({
                reference: 'Description',
                direction: SortDirection.Ascending
            });
            placeOfServiceSorting.add({
                reference: 'Code',
                direction: SortDirection.Ascending
            });

            const [modifier, placeOfService, cases] = await Promise.all(
                [
                    this.lookupService.loadClaimModifiers(undefined, options, []),
                    this.lookupService.loadPlacesOfService(undefined, { all: true, sorting: placeOfServiceSorting }, []),
                    this.service.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes)
                ]
            );

            this.modifiers = modifier.results;
            this.placesOfService = placeOfService.results;
            this.cases = cases.results;
            this.localSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, this.localSorting);
        } catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async insertCaseService(): Promise<boolean> {

        const insertServices: CM.CaseService[] = this.selectedCases.map(memberCase => {
            return this.constructSaveObject(memberCase)
        });

        let isSuccessful = false;
        try {

            this.state.beginSave();
            let progressToast: Toast | undefined;
            if(insertServices.length > 1) {
                progressToast = this.toaster.standard.batch('cm:service.$', insertServices.length)
                progressToast.show();
            }

            const responses = await this.service.insertServiceBatch(insertServices, progressToast);
            isSuccessful = allActionsSucceeded(responses);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('cm:service.$', isSuccessful, insertServices.length);
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    protected async searchServices(value: string): Promise<void> {
        await this.doLoad(async () => {
            if(!value) {
                if(this.serviceSearchResults) this.serviceSearchResults = [];
                return;
            }
            const options: Partial<IQueryLoadOptions> = { top: 30 };
            const includes: string[] = [];

            const serviceSearchFilters: IServiceSearchFilters = { isDental: false, codeOrDescription: value }; // exclude dental services and filter by code or description
            const response = await this.lookupService.searchServices(undefined, options, includes, false, serviceSearchFilters);
            this.serviceSearchResults = response.results;
        });
    }

    @apiCall
    protected async searchUnits(value: string): Promise<void> {
        await this.doLoad(async () => {
            if(!value) {
                if(this.unitSearchResults) this.unitSearchResults = [];
                return;
            }
            const options: Partial<IQueryLoadOptions> = { top: 30 };
            const includes: string[] = [];

            const response = await this.coreService.searchUnits(value, options, includes);
            this.unitSearchResults = response.results;
        });
    }
}
